/**
 * Channel Set entity class
 */

import { ascBy, has, lowerCaseProperty } from '../../../app/helpers/object'
import { dateAsUTC } from '../../../app/helpers/date'

/**
 * ChannelSetFactory
 * returns ChannelSet entity class
 *
 * @class ChannelSetFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.ChannelSet} class
 *
 * @ngdoc factory
 * @name mwApp.factory:ChannelSetFactory
 */
/* @ngInject */
export default function ChannelSetFactory ($http, $injector, Entity, FileUploadMixin, alertLiteSvc, onOffMixin) {
  /* === ChannelSet class === */

  /**
   * ChannelSet model
   *
   * @class ChannelSet
   * @classdesc ChannelSet model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @mixes models.FileUploadMixin
   * @return {models.ChannelSet} ChannelSet entity
   */
  function ChannelSet () {
    const obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  ChannelSet.prototype = Object.create(Entity.prototype)
  ChannelSet.prototype.constructor = ChannelSet

  /* === ChannelSet instance methods & fields === */

  Object.assign(ChannelSet.prototype, {

    /**
     * instance fields
     *
     * @memberof models.ChannelSet#
     * @type {Object}
     */
    _fields: {
      id: Number,
      networkId: Number,
      name: String,
      quantity: Number,
      enabled: true,
      updatedAt: String,
      updatedBy: String,
      addedItems: [],
      channels: [],
      removedItems: [],
      contentItems: [],
      coverImageUrl: String,
      description: String,
      richDescription: String,
      assignedSubscriptions: ['Subscription'],
      type: String,
      pricing: {
        id: Number,
        currency: 'Currency',
        rent: {
          amount: Number,
          autoPay: null,
          dueDate: String,
          paymentMethod: String,
          period: Number,
          periodUnlimited: Boolean,
          rentLengthOverride: Boolean(false),
          timeUnit: String,
          $periodType: 'DEFAULT'
        }
      },
      numberOfCcu: Number,
      exclusivePaymentSystemSetting: {
        id: Number,
        assignedPaymentSystems: [{ paymentSystemId: Number, paymentSystemType: String }]
      },
      $overrideCCU: Boolean,
      pricingType: 'RENT', // @todo hack for next implementation Discount Settings
      $itemType: 'channels',
      $opened: false,
      $rentEnabled: true,
      $setType: 'channel'
    },

    /**
     * url template
     *
     * @memberof models.ChannelSet#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/channelsets/{{::id}}',

    /**
     * url template for available channel sets
     *
     * @memberof models.ChannelSet#
     * @type {String}
     */
    _availableChannelSetsTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/channelsetpricings/extra/channelsets',

    /**
     * public url template
     *
     * @memberof models.ChannelSet#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::networkId}}/contentSets/channel/{{::id}}',

    /**
     * url template for loading content sets
     *
     * @memberof models.channelSet#
     * @type {String}
     */
    _subscriptionUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/channelsets',

    /**
     * url template for checking set intersection
     *
     * @memberof models.channelSet#
     * @type {String}
     */
    _contentIntersectionUrlTemplate: 'api/networks/{{::networkId}}/channelsets/{{::id}}/intersection',

    /**
     * url template for loading custom prices
     *
     * @memberof models.channelSet#
     * @type {String}
     */
    _customPricesUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/channelsets/{{::setId}}/prices',

    /**
     * Load channels extra for Channel set
     *
     * @memberof models.ChannelSet#
     * @returns {Promise} promise
     */
    _getAvailableChannels: function (networkId, type, params = {}) {
      const extraUrl = `api/networks/${networkId}/channelsets/extra/channels?type=${type}&sortBy=name`
      return $http.get(extraUrl, { params })
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },

    _checkForUsageInDiscounts: function () {
      return $http.get(this._url + '/usage')
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },

    /**
     * Prepare object
     *
     * @memberof models.ChannelSet#
     * @override
     */
    _prepare: function () {
      this.$rentEnabled = true
      if (!this.pricing) {
        this.pricing = {
          rent: {
            paymentMethod: 'PER_SET',
            amount: 0,
            period: 1,
            timeUnit: 'DAY'
          }
        }
      }
      if (this.pricing.rent.dueDate) {
        this.pricing.rent.$periodType = 'DUE_DATE'
        this.pricing.rent.$dueDate = new Date(this.pricing.rent.dueDate)
      } else {
        this.pricing.rent.$periodType = 'DEFAULT'
      }

      if (this.channels) {
        this.channels.sort(ascBy(lowerCaseProperty('name')))
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.channelSet#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      if (this.pricing?.rent) {
        switch (this.pricing.rent.$periodType) {
          case 'DUE_DATE':
            this.pricing.rent.dueDate = dateAsUTC(this.pricing.rent.$dueDate, 'YYYY-MM-DD')
            this.pricing.rent.periodUnlimited = false
            this.pricing.rent.period = 0
            this.pricing.rent.timeUnit = null
            this.pricing.rent.autoPay = false
            break
          case 'DEFAULT':
            this.pricing.rent.dueDate = null
            break
          default:
            this.pricing.rent.dueDate = null
            break
        }
      }
      return Entity.prototype._object.apply(this, arguments)
    }
  }, FileUploadMixin, onOffMixin)

  /* === ChannelSet static methods & fields === */

  /**
   * Generate url
   *
   * @param uri
   * @param {Object} [o] - object over which Url will be interpolated
   * @return {String}
   */
  ChannelSet.generateUrl = function (uri = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = cast.prototype.$_urlTemplate(o)
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url
    return url + uri
  }

  /**
   * Get assigned shows
   *
   * @memberof models.ChannelSet#
   */
  ChannelSet.getAssignedChannels = function (tableState, networkId, csId = null) {
    const params = {
      page: tableState.page || 0,
      count: 25
    }

    if (Object.prototype.hasOwnProperty.call(tableState, 'q')) {
      params.q = tableState.q
    }

    let url = `/nora/api/networks/${networkId}/channelsets/extra/channels/assigned`
    if (csId) {
      url = `/nora/api/networks/${networkId}/channelsets/${csId}/extra/channels/assigned`
    }

    return $http.get(url, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get channel sets by params
   *
   * @memberof models.ChannelSet#
   */
  ChannelSet.loadSmartChannelSets = function (tableState, o) {
    o = o || {}

    const url = ChannelSet.generateUrl('', o)

    const params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number
    }
    Object.assign(params, tableState.search.predicateObject)
    if (params.$) {
      params.q = params.$
      delete params.$
    }
    if (params.updatedBy) {
      params['updated-by-like'] = params.updatedBy
      delete params.updatedBy
    }
    if (params.name) {
      params['name-like'] = params.name
      delete params.name
    }

    if (o.searchChannel) {
      params.channel = o.searchChannel.id
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          const result = this.cast(data, {
            // response structure
            content: [this._name],
            first: true,
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            sort: [{
              ascending: true,
              direction: '',
              ignoreCase: true,
              nullHandling: '',
              property: ''
            }],
            totalElements: 0,
            totalPages: 0
          })

          // change SmartTable state
          if (Array.isArray(result.sort) && result.sort.length) {
            const last = result.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = result.number * result.size // first element
          tableState.pagination.number = result.size // page size
          tableState.pagination.numberOfPages = result.totalPages
          tableState.pagination.totalItemCount = result.totalElements

          // return array for SmartTable
          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  ChannelSet.loadChannelSets = function (tableState, o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    const url = cast.prototype.$_availableChannelSetsTemplate(o)

    const params = {
      page: tableState.page || 0,
      count: tableState.count,
      'name-like': o.search || null
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          const result = this.cast(data, {
            // response structure
            content: [cast._name],
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            totalElements: 0,
            totalPages: 0
          })

          tableState.page = result.number
          tableState.count = result.size // page size

          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  // copy all static methods from Entity class
  Object.assign(ChannelSet, Entity)
  ChannelSet._name = 'ChannelSet'
  ChannelSet._publicName = 'Channel Set'
  ChannelSet.init()

  // permissions
  ChannelSet.access = {
    view: '',
    create: '',
    edit: '',
    delete: ''
  }

  return ChannelSet
}
